import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {TEMPLATE_INPUTS} from '../../../common/constants/creativeAssetSpecs'
import type {CreativeAssetState, CreativeStatePatch} from '../../../common/utils/creativeAssetRenderer'
import CreativeImageSlot from './CreativeImageSlot'
import {Slider} from './layerControls'

interface ContentPanelProps {
  state: CreativeAssetState
  onChange: (patch: CreativeStatePatch) => void
}

/** The images the active layout uses — sections for inputs the layout ignores are hidden. */
const ContentPanel = ({state, onChange}: ContentPanelProps) => {
  const {t} = useTranslation()
  const inputs = TEMPLATE_INPUTS[state.template]

  const handleScreenshotLoaded = useCallback(
    (index: number, image: HTMLImageElement) =>
      // Functional update: two loads finishing close together must both land.
      onChange((prev) => {
        const next = [...prev.screenshots]
        next[index] = image
        return {screenshots: next}
      }),
    [onChange],
  )

  const handleScreenshotCleared = useCallback(
    (index: number) => onChange((prev) => ({screenshots: prev.screenshots.filter((_, i) => i !== index)})),
    [onChange],
  )

  const screenshotSlots = Math.min(state.screenshots.length + 1, inputs.screenshots)

  return (
    <div className="caa-layers">
      <h2 className="caa-panel-title">{t('creativeAssets.panels.content')}</h2>

      {inputs.keyArt && (
        <>
          <h3 className="caa-section-title">{t('creativeAssets.sections.keyArt')}</h3>
          <CreativeImageSlot
            slot="key_art"
            label={t('creativeAssets.keyArt.label')}
            image={state.keyArt}
            onImageLoaded={(keyArt) => onChange({keyArt})}
            onClear={() => onChange({keyArt: null, keyArtZoom: 1, keyArtOffsetX: 0, keyArtOffsetY: 0})}
          />
          {state.keyArt && (
            <>
              <Slider
                label={t('creativeAssets.keyArt.zoom')}
                value={state.keyArtZoom}
                min={0.3}
                max={2}
                step={0.01}
                format={(v) => `${Math.round(v * 100)}%`}
                onChange={(keyArtZoom) => onChange({keyArtZoom})}
              />
              <Slider
                label={t('creativeAssets.keyArt.offsetX')}
                value={state.keyArtOffsetX}
                min={-1}
                max={1}
                step={0.01}
                format={(v) => v.toFixed(2)}
                onChange={(keyArtOffsetX) => onChange({keyArtOffsetX})}
              />
              <Slider
                label={t('creativeAssets.keyArt.offsetY')}
                value={state.keyArtOffsetY}
                min={-0.5}
                max={0.5}
                step={0.01}
                format={(v) => v.toFixed(2)}
                onChange={(keyArtOffsetY) => onChange({keyArtOffsetY})}
              />
            </>
          )}
        </>
      )}

      {inputs.screenshots > 0 && (
        <>
          <h3 className="caa-section-title">{t('creativeAssets.sections.screenshots')}</h3>
          {Array.from({length: screenshotSlots}, (_, index) => (
            <CreativeImageSlot
              key={index}
              slot="screenshot"
              label={t('creativeAssets.screenshots.label', {index: index + 1})}
              image={state.screenshots[index] ?? null}
              onImageLoaded={(image) => handleScreenshotLoaded(index, image)}
              onClear={() => handleScreenshotCleared(index)}
            />
          ))}
        </>
      )}

      {inputs.icon && (
        <>
          <h3 className="caa-section-title">{t('creativeAssets.sections.icon')}</h3>
          <CreativeImageSlot
            slot="icon"
            label={t('creativeAssets.icon.label')}
            image={state.icon}
            onImageLoaded={(icon) => onChange({icon})}
            onClear={() => onChange({icon: null})}
          />
        </>
      )}
    </div>
  )
}

export default ContentPanel
